import React, { useState, useContext, useRef } from 'react';
import AppContext from '../../contexts/AppContext';
import { FileText } from 'lucide-react';
import * as firestoreService from '../../services/firestoreService';
import SecurityUtils from '../../utils/security';
import { GAME_TYPES } from '../../utils/constants';

const NewScorecardSetup = () => {
  const { currentUser, setActiveGame, setView, showAlert } = useContext(AppContext);
  // Steps: 1=game type, 2=course search, 3=configure
  const [step, setStep] = useState(1);
  const [selectedGameType, setSelectedGameType] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseName, setCourseName] = useState('');
  const [totalHoles, setTotalHoles] = useState(18);
  const [defaultPar, setDefaultPar] = useState(4);
  const [creating, setCreating] = useState(false);
  const [showManualCourseNote, setShowManualCourseNote] = useState(false);
  const searchTimeout = useRef(null);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim().length < 2) { setSearchResults([]); return; }
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const userLoc = currentUser.location;
        const result = await firestoreService.searchCourses({
          query: query.trim(),
          lat: userLoc?.lat || null,
          lng: userLoc?.lon || null,
        });
        setSearchResults(result.courses || []);
      } catch (err) {
        console.error('Course search error:', err);
        setSearchResults([]);
      }
      setSearching(false);
    }, 400);
  };

  const selectCourse = (course) => {
    setSelectedCourse(course);
    setCourseName(course.clubName || course.courseName);
    if (course.numberOfHoles === 9) setTotalHoles(9);
    else setTotalHoles(18);
    setShowManualCourseNote(false);
    setStep(3);
  };

  const handleStartScorecard = async () => {
    if (!courseName.trim()) { showAlert('Please enter a course name.', 'Error'); return; }
    setCreating(true);
    try {
      const holes = {};
      for (let i = 1; i <= totalHoles; i++) {
        holes[String(i)] = { par: defaultPar, scores: {} };
      }
      const gameData = {
        eventId: null,
        source: 'scorecard',
        gameType: selectedGameType,
        courseName: SecurityUtils.sanitizeText(courseName.trim()),
        players: [{ userId: currentUser.id, name: currentUser.name }],
        teams: [],
        holes,
        totalHoles,
        createdBy: currentUser.id,
        selectedCourseData: selectedCourse ? {
          id: selectedCourse.id,
          parTotal: selectedCourse.parTotal,
          slopeRating: selectedCourse.slopeRating,
          courseRating: selectedCourse.courseRating,
        } : null,
      };
      const created = await firestoreService.createGame(gameData);
      setActiveGame({ id: created.id, ...gameData, status: 'active', currentHole: 1, startedAt: new Date().toISOString() });
      setView('live-score');
    } catch (err) {
      console.error('Error creating scorecard:', err);
      showAlert('Failed to start scorecard.', 'Error');
    }
    setCreating(false);
  };

  return (
    <div className="max-w-2xl mx-auto p-4">
      <h2 className="text-2xl font-bold text-white mb-1 flex items-center space-x-2">
        <FileText className="w-6 h-6 text-[#16a34a]" />
        <span>New Scorecard</span>
      </h2>
      <p className="text-gray-500 text-sm mb-6">Step {step} of 3</p>

      {/* Step 1: Game type */}
      {step === 1 && (
        <div className="space-y-3">
          <p className="text-gray-400 text-sm mb-2">Select a game type</p>
          {GAME_TYPES.map(gt => (
            <button
              key={gt.id}
              onClick={() => { setSelectedGameType(gt.id); setStep(2); }}
              className="w-full p-3 rounded-lg border border-gray-700 hover:border-[#16a34a] text-left transition-colors"
            >
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{gt.emoji}</span>
                <div>
                  <p className="text-white font-bold">{gt.name}</p>
                  <p className="text-gray-400 text-sm">{gt.desc}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Step 2: Course search */}
      {step === 2 && (
        <div className="space-y-4">
          <p className="text-gray-400 text-sm">Search for your golf course</p>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search by course name or city..."
            className="w-full px-3 py-3 border border-gray-700 rounded-lg text-white bg-black focus:ring-2 focus:ring-[#16a34a]"
            autoFocus
          />
          {searching && (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-[#16a34a]"></div>
            </div>
          )}
          {searchResults.length > 0 && (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {searchResults.map((course) => (
                <button
                  key={course.id}
                  onClick={() => selectCourse(course)}
                  className="w-full p-3 rounded-lg border border-gray-700 hover:border-[#16a34a] text-left transition-colors"
                >
                  <p className="text-white font-bold">{course.clubName}</p>
                  <p className="text-gray-400 text-sm">
                    {course.city}, {course.state}
                    {course.distance !== null && course.distance !== undefined && ` · ${course.distance} mi`}
                  </p>
                  <div className="flex space-x-3 mt-1 text-xs text-gray-500">
                    {course.parTotal && <span>Par {course.parTotal}</span>}
                    {course.numberOfHoles && <span>{course.numberOfHoles} holes</span>}
                    {course.slopeRating && <span>Slope {course.slopeRating}</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
          {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
            <p className="text-gray-500 text-sm text-center py-4">No courses found</p>
          )}
          <div className="flex space-x-2">
            <button onClick={() => setStep(1)} className="flex-1 py-2 bg-gray-700 text-white rounded-lg font-bold">Back</button>
            <button onClick={() => { setSelectedCourse(null); setShowManualCourseNote(false); setStep(3); }} className="flex-1 py-2 text-gray-400 hover:text-white rounded-lg text-sm border border-gray-700">
              Skip — enter manually
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Configure */}
      {step === 3 && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white mb-1">Course Name</label>
            <input
              type="text"
              value={courseName}
              onChange={(e) => { setCourseName(e.target.value); setShowManualCourseNote(false); }}
              onBlur={() => { if (!selectedCourse && courseName.trim()) setShowManualCourseNote(true); }}
              className="w-full px-3 py-2 border border-gray-700 rounded-lg text-white bg-black focus:ring-2 focus:ring-[#16a34a]"
              placeholder="Course name"
            />
            {showManualCourseNote && (
              <p className="text-yellow-400 text-xs mt-1">
                This course wasn't selected from the course list, so your handicap will be calculated using a simplified formula that doesn't include course rating or slope rating.
              </p>
            )}
          </div>
          {selectedCourse && (
            <div className="bg-gray-900 rounded-lg p-3 text-sm">
              <p className="text-gray-400">
                {selectedCourse.city}, {selectedCourse.state}
                {selectedCourse.parTotal ? ` · Par ${selectedCourse.parTotal}` : ''}
                {selectedCourse.slopeRating ? ` · Slope ${selectedCourse.slopeRating}` : ''}
                {selectedCourse.totalYards ? ` · ${selectedCourse.totalYards} yds` : ''}
              </p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white mb-1">Holes</label>
              <select value={totalHoles} onChange={(e) => setTotalHoles(Number(e.target.value))} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-white bg-black">
                <option value={9}>9 Holes</option>
                <option value={18}>18 Holes</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">Default Par</label>
              <select value={defaultPar} onChange={(e) => setDefaultPar(Number(e.target.value))} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-white bg-black">
                <option value={3}>Par 3</option>
                <option value={4}>Par 4</option>
                <option value={5}>Par 5</option>
              </select>
            </div>
          </div>
          <div className="flex space-x-2 pt-2">
            <button onClick={() => setStep(2)} className="flex-1 py-3 bg-gray-700 text-white rounded-lg font-bold">Back</button>
            <button
              onClick={handleStartScorecard}
              disabled={creating || !courseName.trim()}
              className="flex-1 py-3 bg-[#16a34a] text-white rounded-lg font-bold disabled:opacity-50"
            >
              {creating ? 'Starting...' : 'Start Round'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== GAME TRACKING ====================

export default NewScorecardSetup;
